<template>
    <div>
      <v-card flat>
        <v-data-table
          :headers="headers"
          :items="filteredMedications"
          :items-per-page="10"
          class="elevation-0 custom-data-table"
          :no-data-text="noDataText"
          :height="tableHeight"
          fixed-header
          :loading="tableLoading"
          density="compact"
        >
          <template v-slot:top>
            <div class="d-flex justify-space-between align-center pa-4">
              <span class="text-h5">Medications</span>
              <div class="d-flex align-center">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  variant="outlined"
                  density="compact"
                  style="width: 350px; margin-right: 16px"
                ></v-text-field>
                <v-btn
                  color="primary"
                  prepend-icon="mdi-plus"
                  @click="openCreateDialog"
                >
                  Create Medication
                </v-btn>
              </div>
            </div>
          </template>
  
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="editMedication(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              @click="deleteMedication(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- Create Medication Dialog -->
      <CreateMedicationDialog
        v-model="showCreateDialog"
        @created="handleMedicationCreated"
      />
  
      <!-- Edit Medication Dialog -->
      <EditMedicationDialog
        v-model="showEditDialog"
        :medication="selectedMedication"
        @updated="handleMedicationUpdated"
      />
  
      <!-- Delete Medication Dialog -->
      <DeleteMedicationDialog
        v-model="showDeleteDialog"
        :medication="selectedMedication"
        @deleted="handleMedicationDeleted"
      />
    </div>
  </template>
  
  <script>
  import CreateMedicationDialog from "@/components/Medication/Dailogs/CreateMedicationDialog.vue";
  import EditMedicationDialog from "@/components/DrugLibrary/Dialog/EditMedicationDialog.vue";
  import DeleteMedicationDialog from "@/components/DrugLibrary/Dialog/DeleteMedicationDialog.vue";
  import GetMedications from "@/mixins/Medication/GetMedications";
  
  export default {
    name: "MedicationView",
    mixins: [GetMedications],
    components: {
      CreateMedicationDialog,
      EditMedicationDialog,
      DeleteMedicationDialog,
    },
    data() {
      return {
        showCreateDialog: false,
        showEditDialog: false,
        showDeleteDialog: false,
        tableLoading: false,
        selectedMedication: null,
        selectedIndex: -1,
        medications: [],
        search: "",
        windowHeight: window.innerHeight,
        headers: [
          { title: "Medication Name", key: "medication_name", sortable: true },
          { title: "Medication Class", key: "medication_class", sortable: true },
          { title: "Brand Name", key: "brand_name", sortable: true },
          { title: "Actions", key: "actions", sortable: false, align: "center" },
        ],
      };
    },
    computed: {
      noDataText() {
        return "No medications found. Click 'Create Medication' to add one.";
      },
      tableHeight() {
        return this.windowHeight - 280;
      },
      filteredMedications() {
        if (!this.search) {
          return this.medications;
        }
  
        const searchTerm = this.search.toLowerCase().trim();
        return this.medications.filter((med) => {
          return (
            (med.medication_name &&
              med.medication_name.toLowerCase().includes(searchTerm)) ||
            (med.medication_class &&
              med.medication_class.toLowerCase().includes(searchTerm)) ||
            (med.brand_name &&
              med.brand_name.toLowerCase().includes(searchTerm))
          );
        });
      },
    },
    async mounted() {
      this.updateWindowHeight();
      window.addEventListener("resize", this.updateWindowHeight);
      this.loadMedications();
     
    },
    beforeUnmount() {
      window.removeEventListener("resize", this.updateWindowHeight);
    },
    methods: {
      onTabActivated() {
        this.loadMedications();
      },
      async loadMedications() {
        this.tableLoading = true;
        try {
          await this.fetchMedications();
          this.medications = this.medicationsList;
        } catch (error) {
          console.error("Error loading medications:", error);
        } finally {
          this.tableLoading = false;
        }
      },
      updateWindowHeight() {
        this.windowHeight = window.innerHeight;
      },
      openCreateDialog() {
        this.showCreateDialog = true;
      },
      async handleMedicationCreated() {
        // Refetch the medication list from API
        this.tableLoading = true;
        try {
          await this.fetchMedications();
          this.medications = this.medicationsList;
        } catch (error) {
          console.error("Error loading medications:", error);
        } finally {
          this.tableLoading = false;
        }
      },
      editMedication(med) {
        this.selectedMedication = { ...med };
        // Find the actual index in the original medications array
        this.selectedIndex = this.medications.findIndex(
          (m) => m.medication_master_id === med.medication_master_id
        );
        this.showEditDialog = true;
      },
      async handleMedicationUpdated() {
        // Refetch the medication list from API
        this.tableLoading = true;
        try {
          await this.fetchMedications();
          this.medications = this.medicationsList;
        } catch (error) {
          console.error("Error loading medications:", error);
        } finally {
          this.tableLoading = false;
        }
        this.selectedIndex = -1;
        this.selectedMedication = null;
      },
      deleteMedication(med) {
        this.selectedMedication = { ...med };
        // Find the actual index in the original medications array
        this.selectedIndex = this.medications.findIndex(
          (m) => m.medication_master_id === med.medication_master_id
        );
        this.showDeleteDialog = true;
      },
      async handleMedicationDeleted() {
        // Refetch the medication list from API
        this.tableLoading = true;
        try {
          await this.fetchMedications();
          this.medications = this.medicationsList;
        } catch (error) {
          console.error("Error loading medications:", error);
        } finally {
          this.tableLoading = false;
        }
        this.selectedIndex = -1;
        this.selectedMedication = null;
      },
    },
  };
  </script>
  
  